import { navSubjectFrom } from '../navSubject'
import { useSession } from '../useSession'
import PortalShell from './PortalShell'
import PortalView from './PortalView'
import TeacherPortalView from './TeacherPortalView'
import TodayForMe from './TodayForMe'

/**
 * Home, for whoever is looking at it (D-322, D-326).
 *
 * **This is the other half of retiring the portal switch.** Schedule already
 * reached a dual-role account's whole week — stacked then, two named views now
 * (D-352) — while Home did not, so she still had to choose, and the switch was
 * still there for her to choose with. D-309's
 * control existed because two portals had two homes; the fix is one home that
 * holds both, not a better way to pick between them.
 *
 * **Both, stacked, with headings only in that case.** A teacher who is not a
 * parent should see her evening, not a section called "What you are teaching"
 * with nothing beside it to be distinguished from.
 *
 * **`ScheduleScreen` no longer does this, and the difference is deliberate**
 * (D-352). Schedule is a destination with a view bar, so its halves could
 * become two addresses with a control naming them; Home is a single-screen
 * destination with no second-level row, and Rule 3 says that row is earned.
 * Splitting Home would mean inventing one, or a remembered mode — which is the
 * portal switch coming back through the side door. **So do not "restore
 * parity" here by copying Schedule.** What is genuinely open is that nobody has
 * measured Home for the same reader; if it turns out as deep, the answer is a
 * measurement and its own decision, not a second implementation of Schedule's.
 *
 * ⚠ **This screen is not where Tonya lands.** `landingFor` sends anyone holding
 * `ViewAllSchedules` to `/dashboard` (D-878), which is the screen the proposal
 * mocked up and which has no rail row of its own (D-884) — *Home* is the row
 * that lights while she is on it. So this is the teacher's and the parent's
 * home, and `TodayForMe` answers *what needs me* scoped to her.
 *
 * *It read "Tonya still lands on the calendar, until M-dashboard builds the one
 * the proposal mocked up" — true when written, wrong from D-878 onward, and the
 * only other comment in the SPA about where she lands.*
 */
export default function HomeScreen() {
  const { session } = useSession()
  if (!session) return null

  const subject = navSubjectFrom(session)
  const firstName = session.displayName?.trim().split(" ")[0] || "there"
  /*
   * <b>A dancer with her own login has a day too</b> (D-572).
   *
   * This asked <c>isParent</c> alone, so Ava - a student teacher who dances -
   * got <i>You are teaching: nothing today</i> and no sign of the five classes
   * her Schedule tab was listing, and a dancer who teaches nothing fell all the
   * way through to <i>Nothing here yet</i> while her rail still offered her a
   * week. Reported from the deployed site.
   *
   * <b>And the panel below is hers too now</b> (D-918). It used to stay on
   * <c>isParent</c>, so a dancer's own login had her day and no sign of her
   * attendance, her standing or her makeups - reported from the studio floor,
   * <c>asked</c> 2026-10-20: <i>"they can't see their attendance
   * records/standing with that at all, we need to make it so that they can
   * view it the same as the parent account."</i>
   *
   * ⚠ <b>The household is not in this panel and never was</b>, which is what
   * makes handing it over safe: other children, the address and the guardians
   * live in <c>HouseholdPanel</c> on <c>/account</c>, behind its own
   * <c>isParent</c> gate. This comment used to argue the opposite - that the
   * panel <i>was</i> the household view - and that reading is why the screen
   * stayed broken for her.
   *
   * <b>What she gets is reading, not acting.</b> Claiming a makeup, calling
   * off a private, saying she will be out: all behind
   * <c>ArrangeAttendance</c> and <c>BookPrivates</c> (D-920), which Parent
   * holds and Dancer does not, until a
   * parent grants it (D-918). <c>asked</c>: <i>"for say older students that
   * want/need the ability to schedule privates and such they could do that,
   * but it definitely be off by default."</i> So the branch is <c>dances</c>
   * and the controls inside it ask the capability. See D-572, now settled.
   */
  const dances = subject.isParent || subject.isDancer
  const both = subject.teaches && dances

  return (
    <>
      {/*
        Tonight, one line a person, above both halves (D-380).

        **It renders for everybody, not only the dual-role account.** A parent
        gets a row per child, a teacher gets her own — the shape is the same,
        which is what makes this one screen rather than three. What it never
        becomes is a grid: that design was four columns and 80 pixels each on a
        phone for a teacher with three children dancing.
      */}
      {/*
        **One shell around the whole page, not one per section.**

        Every block used to bring its own `portal-shell`, which sets both the
        measure and the padding — so nested inside each other they doubled it.
        Measured at 820px: three different left edges, 94, 111 and 118, and
        headings floating a full 24px away from what they head. Removing the
        inner shells without adding an outer one then sent the cards to the
        window edge. It is one container now, and everything inside inherits it.
      */}
      {(subject.teaches || dances) && (
        /*
          Shared with `/me` and `/teacher`, which render these same two portals
          standalone and need the identical measure. It was spelled out here as
          a class string and they were left with none at all, which drew the
          parent's portal at the full width of the content area.
        */
        <PortalShell>
          {/*
            The page heading, once, at the top (D-380).

            Both portals used to greet her, so a dual-role account was greeted
            twice — and removing both left Home with no `h1` at all, which is
            worse than the duplicate and is how the first attempt at this failed.
            First name only, because "Hi, Heather" is how the studio talks and
            "Welcome, Heather Bennett" is how a utility bill does.
          */}
          <h1 className="mb-3 font-display text-xl">Hi, {firstName}</h1>

          <TodayForMe />

          {subject.teaches && (
            <section>
              {/*
                Named for what is under it now (D-380). It read "What you are
                teaching" and sat above the cover panel — a list of classes she
                is *out* for — because the schedule it used to head has gone to
                the Today panel.
              */}
              {/*
                **The heading moved inside** (D-415). It used to render here on
                `both` alone, and every panel under it decides its own
                emptiness — two by returning `null` — so a dual-role teacher
                with nothing waiting got *Needs an answer* over empty space.
                Reported from the deployed site.

                Passed rather than rendered, because whether there is a heading
                at all is this screen's business (only a dual-role account needs
                one) and whether there is anything to head is the portal's.
              */}
              <TeacherPortalView heading={both ? 'Needs an answer' : undefined} />
            </section>
          )}

          {dances && (
            <section>
              {both && <h2 className="mt-6 mb-2 font-display text-lg">Your dancers</h2>}
              {/*
                <b>What is waiting on her is drawn once</b> (D-767). Both
                portals draw the ask list, and a dual-role account gets both
                portals - so every row she had to answer appeared under *Needs
                an answer* and again under *Your dancers*, two identical cards
                with the same Yes and No. Reported from the deployed site.

                The teaching half keeps them, because *Needs an answer* is
                exactly what they are; her parent half keeps what she is
                waiting on, which the teaching half deliberately never draws.
              */}
              <PortalView showGreeting={false} askParts={both ? ['requester'] : undefined} />
            </section>
          )}
        </PortalShell>
      )}

      {/*
        Neither, and not an admin either - an account linked to nobody. The rail
        gives her Home alone (D-322), so this is the whole screen and it has to
        say something true rather than nothing.
      */}
      {!subject.teaches && !dances && (
        <div className="portal-shell py-10 text-center">
          <h1 className="font-display text-xl">Nothing here yet</h1>
          <p className="mt-2 text-[13.5px] text-ink-3">
            Your account is not linked to a dancer or to any classes. The studio can sort that
            out.
          </p>
        </div>
      )}
    </>
  )
}
